import styled from '@emotion/styled';
import { useLocation, useNavigate } from 'react-router-dom';
import { Badge, Popover, Tooltip } from 'antd';
import {
  InvitePractitioner,
  InviteUser,
} from 'src/components/groupComponents/inviteUser';
import { useFeatureEntitlements } from '@frontegg/react';
import { Button } from '@insight-health-ai/roseate/components';
import {
  useGetCurrentPractitioner,
  useGetCurrentPractitionerEntitlements,
} from 'src/hooks/settings';
import { ProTrialCountDown } from 'src/components/groupComponents/proTrialCountdown';
import { AppNavMenu, useAppNavMenus } from '..';
import { RippleDot } from 'src/components';
import { UserMenu } from './userMenu';
import { WorkspaceLogo, WorkspacePlan } from './workspaceLogo';
import { useTheme } from '@insight-health-ai/roseate/theme';
import { ProIconSvg } from 'src/icons/Pro.icon';
import { UsergroupAddOutlined } from '@ant-design/icons';

export const IconHalo = styled.div<{ isActive: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 16px;
  font-size: ${({ theme }) => theme.fontSize.lg['0']};
  background-color: ${({ theme }) => theme.colors.secondary[50]};
  color: ${({ theme }) => theme.colors.secondary[500]};
`;

export const MenuLabel = styled.div<{ isActive: boolean }>`
  font-size: 0.7rem;
  text-align: center;
  max-width: 56px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const ItemWrapper = styled.div<{ isActive: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: start;
  cursor: pointer;
  transition: all 0.2s ease;
  gap: 2px;

  ${({ isActive, theme }) =>
    isActive
      ? `
    ${IconHalo} {
      background-color: ${theme.colors.primary[100]};
      color: ${theme.colors.primary[800]};
      box-shadow: ${theme.boxShadow.inner};
    }
    ${MenuLabel} {
      color: ${theme.colors.primary[800]};
    }
  `
      : ''}

  &:hover {
    ${IconHalo} {
      font-size: ${({ theme }) => theme.fontSize.xl['0']};
    }
    ${({ isActive, theme }) =>
      isActive
        ? ``
        : `
        ${IconHalo} {
          background-color: ${theme.colors.secondary[200]};
          color: unset;
        }
      `}
  }
  &:empty {
    display: none;
  }
`;

const WithBadge = ({
  type,
  children,
}: {
  type: 'dot' | undefined;
  children: React.ReactNode;
}) => {
  if (type === 'dot') {
    return (
      <Badge
        offset={[-5, 5]}
        count={<RippleDot size="sm" appearance="alternate" />}
      >
        {children}
      </Badge>
    );
  }
  return children;
};

export const MenuItem = ({
  menu,
  style,
  isMobile,
}: {
  menu: AppNavMenu;
  style?: React.CSSProperties;
  isMobile?: boolean;
}) => {
  const { tourPopup, onClick, path, submenu, title, icon, showBadge, tooltip } =
    menu;
  const { theme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const pathSegments = location.pathname.split('/').filter(Boolean);
  const menuSegments = path.split('/').filter(Boolean);
  const isActive =
    menuSegments.length > 0 &&
    menuSegments.every((segment, index) => pathSegments[index] === segment);

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else if (path) {
      navigate(path);
    }
  };

  let menuItem = (
    <span tabIndex={0}>
      <WithBadge type={showBadge}>
        <ItemWrapper onClick={handleClick} isActive={isActive} style={style}>
          <IconHalo isActive={isActive}>{icon}</IconHalo>
          <MenuLabel
            className={isMobile ? 'text-nowrap' : ''}
            isActive={isActive}
          >
            {title}
          </MenuLabel>
        </ItemWrapper>
      </WithBadge>
    </span>
  );

  if (tourPopup?.open) {
    menuItem = (
      <Popover
        open={tourPopup.open}
        onOpenChange={tourPopup.onOpenChange}
        content={
          <div className="max-w-[300px]">
            {tourPopup.content}
            <div className="flex justify-end empty:hidden">
              {tourPopup.secondaryAction && (
                <Button
                  variant="outlined"
                  {...tourPopup.secondaryAction}
                  size="sm"
                />
              )}
              {tourPopup.primaryAction && (
                <Button {...tourPopup.primaryAction} size="sm" />
              )}
            </div>
          </div>
        }
        title={tourPopup.title}
        placement={isMobile ? 'top' : 'right'}
      >
        {menuItem}
      </Popover>
    );
  }

  if (isMobile) {
    return menuItem;
  }

  if (submenu?.length) {
    return (
      <Popover
        trigger={['hover', 'focus']}
        styles={{
          body: {
            padding: theme.spacing[2],
          },
        }}
        content={
          <div className="flex flex-col gap-1 min-w-[150px]">
            <div className="text-sm font-medium pb-2 border-b border-gray-200">
              {title}
            </div>
            <div className="flex flex-col">
              {submenu.map((subItem) => {
                const { icon, title, path } = subItem;
                return (
                  <div
                    onClick={() => navigate(path)}
                    tabIndex={0}
                    key={subItem.path}
                    className="flex items-center gap-2 hover:bg-gray-200 p-2 rounded-md cursor-pointer w-full"
                  >
                    {icon}
                    {title}
                  </div>
                );
              })}
            </div>
          </div>
        }
        placement="right"
      >
        {menuItem}
      </Popover>
    );
  }

  return (
    <Tooltip title={tooltip || title} placement="right">
      {menuItem}
    </Tooltip>
  );
};

export const SidenavQuickActions = ({ isMobile }: { isMobile?: boolean }) => {
  const { isAdmin } = useGetCurrentPractitioner();
  const { isEntitled: hasIntakeAccess } = useFeatureEntitlements('intake');
  const { isEntitled: hasFollowupAccess } = useFeatureEntitlements('followup');
  const { isEntitled: isInviteUserEntitled } =
    useFeatureEntitlements('invite-user');
  const { isEntitled: hasPhoneTriageAccess } =
    useFeatureEntitlements('phone-triage');

  const { isAuraPro, isAuraTeams, isPLGUser } =
    useGetCurrentPractitionerEntitlements();
  const showProTrialCountDown = !isAuraPro && isPLGUser;

  const hasInviteUserAccess = isInviteUserEntitled && isAdmin;
  const showInviteUserWithMirrorOption =
    hasIntakeAccess ||
    hasFollowupAccess ||
    isAuraPro ||
    isAuraTeams ||
    hasPhoneTriageAccess;
  return (
    <>
      {showProTrialCountDown && (
        <ProTrialCountDown
          getCustomTriggerJsx={({
            onClick,
            label,
          }: {
            onClick: () => void;
            label: string;
          }) => {
            if (isMobile) {
              return (
                <Button
                  appearance="secondary"
                  onClick={onClick}
                  icon={<ProIconSvg size={16} fill="currentColor" />}
                  variant="outlined"
                  className="rounded-full"
                >
                  {label}
                </Button>
              );
            }
            return (
              <MenuItem
                menu={{
                  title: '',
                  path: '',
                  onClick,
                  tooltip: label,
                  icon: <ProIconSvg size={20} fill="currentColor" />,
                }}
              />
            );
          }}
        />
      )}
      {hasInviteUserAccess &&
        (showInviteUserWithMirrorOption ? (
          <InvitePractitioner
            getCustomTriggerJsx={({
              onClick,
              label,
            }: {
              onClick: () => void;
              label: string;
            }) => {
              if (isMobile) {
                return (
                  <Button
                    appearance="secondary"
                    onClick={onClick}
                    icon={<UsergroupAddOutlined />}
                    variant="outlined"
                    className="rounded-full"
                  >
                    {label}
                  </Button>
                );
              }
              return (
                <MenuItem
                  menu={{
                    title: '',
                    path: '',
                    onClick,
                    tooltip: label,
                    icon: <UsergroupAddOutlined />,
                  }}
                />
              );
            }}
          />
        ) : (
          <InviteUser
            getCustomTriggerJsx={({
              onClick,
              label,
            }: {
              onClick: () => void;
              label: string;
            }) => (
              <MenuItem
                menu={{
                  title: '',
                  path: '',
                  onClick,
                  tooltip: label,
                  icon: <UsergroupAddOutlined />,
                }}
              />
            )}
            isMinimized
          />
        ))}
    </>
  );
};

export const SideNav = () => {
  const menus = useAppNavMenus();

  return (
    <div className="flex flex-col h-full overflow-hidden items-center w-[80px] flex-shrink-0 bg-white shadow-md">
      <div className="px-2 py-4 flex flex-col items-center justify-center gap-2 w-full flex-shrink-0">
        <WorkspaceLogo />
        <WorkspacePlan />
      </div>
      <div className="py-2 flex flex-col items-center gap-3 w-full overflow-y-auto overflow-x-hidden">
        {menus.map((menu) => (
          <MenuItem key={menu.path} menu={menu} />
        ))}
      </div>

      <div className="px-2 py-4 mt-auto w-full flex flex-col items-center gap-2 border-t border-gray-200 flex-shrink-0 empty:hidden">
        <SidenavQuickActions />
        <UserMenu />
      </div>
    </div>
  );
};
